import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AirplaneIcon, AirplaneLandingIcon, AirplaneTakeoffIcon } from '@phosphor-icons/react';
import { knownCityByAirport } from '@voll-intelligence/domain';
import { AirlineLogo } from '@voll-intelligence/intelligence-ui-kit';
import type { ChatUiFlightSegment, CheckoutItem } from '@voll-intelligence/genui-contract';
import type { FlightDetailSummary } from '../../../../shared/ui/action.js';
import { SegmentDetailDrawer } from '../../../../shared/ui/flight_segment.js';
import { useLayerHost } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import { FlightDetailDrawerContainer } from '../../../../flight/shared/flight_detail_drawer_container.js';
import {
  flightNumberLabel,
  segmentStops,
  stopCondition,
} from '../../../../shared/ui/flight_leg.js';
import {
  arrivalDayOffset,
  fmtWeekdayDayMonth,
  fmtTime,
  nextDayArrivalLabel,
} from '../../../../shared/ui/flight_format.js';
import { useGenUiI18n, type GenUiTranslate } from '../../../../shared/ui/i18n.js';
import {
  CheckoutTripLeg,
  CheckoutTripLegs,
  RULE_LINK,
  StopsPill,
} from '../../ui/checkout_trip_leg.js';

/**
 * Corpo de voo do card `product_checkout` — o «Trecho do voo» do Figma final do
 * 1click (#5280, 17/10). As três formas divergem só em dado: ida+volta é UM
 * item com `return` (pernas lado a lado quando o card é largo), só ida é UM item
 * sem `return`, multi trecho são N itens one-way, empilhados. Nada aqui é
 * compartilhado com a `FlightLeg` da lista de ofertas/recibos de propósito.
 */
export type FlightCheckoutItem = Extract<CheckoutItem, { productType: 'flight' }>;

interface CheckoutLeg {
  readonly seg: ChatUiFlightSegment;
  readonly label: string;
  readonly offerId: string;
  /** A volta usa `AirplaneLanding`; ida e cada trecho, `AirplaneTakeoff`. */
  readonly inbound: boolean;
  /** A tarifa do trecho, na régua. Ida e volta repetem a `fareClass` única do
   *  item (é a tarifa do itinerário), salvo a volta vendida à parte, que leva a
   *  `returnFareClass` dela (#3844). */
  readonly fare: string | undefined;
}

/** Achata os itens nas pernas exibidas, com o sentido e a tarifa de cada uma. */
function flightCheckoutLegs(
  items: readonly FlightCheckoutItem[],
  t: GenUiTranslate,
): CheckoutLeg[] {
  const single = items.length === 1 ? items[0] : undefined;
  if (single) {
    const { detail, offerId } = single;
    const outbound: CheckoutLeg = {
      seg: detail.outbound,
      label: t('flight.quote.outbound'),
      offerId,
      inbound: false,
      fare: detail.fareClass,
    };
    if (!detail.return) return [outbound];
    return [
      outbound,
      {
        seg: detail.return,
        label: t('flight.quote.return'),
        offerId,
        inbound: true,
        fare: detail.returnFareClass ?? detail.fareClass,
      },
    ];
  }
  // N itens são one-way por contrato do emissor (`buildFlightCheckoutBlockMulti`,
  // apps/services/agents/src/domains/flight/ui/ui.ts, só popula `outbound`); um
  // eventual `detail.return` num item aqui não seria renderizado.
  return items.map((it, i) => ({
    seg: it.detail.outbound,
    label: t('flight.reservation.leg', { n: i + 1 }),
    offerId: it.offerId,
    inbound: false,
    fare: it.detail.fareClass,
  }));
}

export function FlightCheckoutLegs({ items }: { readonly items: readonly FlightCheckoutItem[] }) {
  const { t } = useGenUiI18n();
  const [open, setOpen] = useState<CheckoutLeg | null>(null);
  const [seats, setSeats] = useState<CheckoutLeg | null>(null);
  const layerHost = useLayerHost();
  const legs = flightCheckoutLegs(items, t);
  // Só o PAR ida/volta ganha as duas colunas: a simetria é o dado.
  const pair = items.length === 1 && Boolean(items[0]?.detail.return);
  return (
    <>
      <CheckoutTripLegs pair={pair}>
        {legs.map((leg, i) => (
          <FlightLeg
            // `offerId` é único por item; o índice desempata o par ida/volta,
            // que compartilha o mesmo item (e por extensão o mesmo offerId).
            key={`${leg.offerId}-${String(i)}`}
            leg={leg}
            onDetail={() => {
              setOpen(leg);
            }}
            onSeats={() => {
              setSeats(leg);
            }}
          />
        ))}
      </CheckoutTripLegs>
      {open && (
        <SegmentDetailDrawer
          seg={open.seg}
          dir={open.label}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {seats &&
        createPortal(
          /* O portal monta fora da raiz `.genui2` do host, e as custom properties do
           * DS só herdam por ancestral — daí a classe no wrapper. */
          <div className="genui2">
            {/* Só consulta (#5331): sem `onSelect`, o drawer não tem rodapé de
                escolha, e a tela cheia também não troca assento: só o agente. */}
            <FlightDetailDrawerContainer
              offerId={seats.offerId}
              summary={legSummary(seats)}
              focus="seats"
              onClose={() => {
                setSeats(null);
              }}
            />
          </div>,
          layerHost,
        )}
    </>
  );
}

/** O cabeçalho do drawer antes de as pernas chegarem, da perna que abriu. */
function legSummary({ seg, inbound }: CheckoutLeg): FlightDetailSummary {
  return {
    ...(seg.carrier ? { carrier: seg.carrier } : {}),
    ...(seg.carrierName ? { carrierName: seg.carrierName } : {}),
    ...(seg.flightNumber ? { flightNumber: seg.flightNumber } : {}),
    from: seg.from,
    to: seg.to,
    dir: inbound ? 'return' : 'outbound',
  };
}

function FlightLeg({
  leg,
  onDetail,
  onSeats,
}: {
  readonly leg: CheckoutLeg;
  readonly onDetail: () => void;
  readonly onSeats: () => void;
}) {
  const { t, locale } = useGenUiI18n();
  const { seg } = leg;
  const date = fmtWeekdayDayMonth(seg.departAt, locale);
  const days = arrivalDayOffset(seg.departAt, seg.arriveAt);
  const Glyph = leg.inbound ? AirplaneLandingIcon : AirplaneTakeoffIcon;
  return (
    <div role="group" aria-label={`${leg.label} · ${date}`}>
      <CheckoutTripLeg
        icon={<Glyph size={16} weight="bold" />}
        date={date}
        direction={leg.label}
        operator={
          <>
            <AirlineLogo
              {...(seg.carrier ? { iata: seg.carrier } : {})}
              {...(seg.carrierName ? { name: seg.carrierName } : {})}
              size={20}
            />
            <span className="truncate tabular-nums">
              {[flightNumberLabel(seg.carrier, seg.flightNumber), seg.aircraftName]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        }
        departure={{
          time: fmtTime(seg.departAt),
          code: seg.from,
          place: knownCityByAirport(seg.from, locale) ?? undefined,
        }}
        arrival={{
          time: fmtTime(seg.arriveAt),
          code: seg.to,
          place: knownCityByAirport(seg.to, locale) ?? undefined,
        }}
        arrivalDayOffset={{ days, label: nextDayArrivalLabel(days, locale) }}
        rail={<FlightRail seg={seg} />}
        duration={seg.durationLabel}
        rule={leg.fare}
        links={
          <>
            <button type="button" className={RULE_LINK} onClick={onDetail}>
              {t('flight.segment.viewDetail')}
            </button>
            <button type="button" className={RULE_LINK} onClick={onSeats}>
              {t('flightOffers.seats')}
            </button>
          </>
        }
      />
    </div>
  );
}

/** O meio do trilho: o avião no voo direto; com escalas, a pill com a contagem
 *  e as siglas («2 paradas · GRU, PMN»). A espera de cada escala fica na ficha
 *  do trecho («Detalhes»), decisão do Victor sobre o Figma de 17/10. */
function FlightRail({ seg }: { readonly seg: ChatUiFlightSegment }) {
  const { t } = useGenUiI18n();
  const stops = segmentStops(seg);
  if (stops <= 0) {
    return (
      <>
        <AirplaneIcon size={14} weight="fill" className="rotate-90" aria-hidden />
        <span className="sr-only">{stopCondition(stops, t)}</span>
      </>
    );
  }
  const via = (seg.connections ?? []).map((c) => c.iata).join(', ');
  return <StopsPill>{[stopCondition(stops, t), via].filter(Boolean).join(' · ')}</StopsPill>;
}
